/**
 * WarningsList: expands from the LiveCountersBar's warning indicator
 * (PRD §8.3). Dismissible per-session only — dismissal never alters
 * underlying data, it's purely a "stop showing me this one for now" UI
 * affordance, clearly labeled as such so a coordinator never wonders if
 * clicking it changed their layout. Clicking a warning highlights the
 * affected object(s) on canvas AND closes the panel, since the entire
 * point of clicking it is to go look at the canvas — leaving the panel
 * open afterward would cover the exact thing the coordinator just asked
 * to see.
 *
 * Grouped by SEVERITY now, not by rule category — per direct coordinator
 * feedback, severity is the dimension that actually matters here.
 * Critical issues lead, get the most visual weight, and are always
 * expanded. Warning issues follow, expanded but quieter. Info issues are
 * collapsed by default — the brief is explicit that these "should simply
 * exist as recommendations if the coordinator chooses to inspect them,"
 * not compete for attention with anything that actually needs it.
 */

import { useState } from 'react';
import type { ValidationIssue } from '../../domain/types';
import { useEditorStore } from '../../store/useEditorStore';

interface WarningsListProps {
  criticalIssues: ValidationIssue[];
  warningIssues: ValidationIssue[];
  infoIssues: ValidationIssue[];
  onClose: () => void;
}

export function WarningsList({ criticalIssues, warningIssues, infoIssues, onClose }: WarningsListProps) {
  const selectMultiple = useEditorStore((state) => state.selectMultiple);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [infoExpanded, setInfoExpanded] = useState(false);

  const visibleCritical = criticalIssues.filter((i) => !dismissedIds.has(i.id));
  const visibleWarning = warningIssues.filter((i) => !dismissedIds.has(i.id));
  const visibleInfo = infoIssues.filter((i) => !dismissedIds.has(i.id));
  const totalVisible = visibleCritical.length + visibleWarning.length + visibleInfo.length;

  function handleDismiss(id: string) {
    setDismissedIds((prev) => new Set(prev).add(id));
  }

  function handleHighlight(issue: ValidationIssue) {
    if (issue.relatedObjectIds.length > 0) {
      selectMultiple(issue.relatedObjectIds);
    }
    onClose();
  }

  if (totalVisible === 0) {
    return (
      <div className="absolute bottom-full left-0 right-0 border-t border-ivory-300 bg-white p-4 shadow-elevated">
        <p className="font-body text-sm text-ink-400">
          You&rsquo;ve hidden everything for now. It&rsquo;ll come back next time you reopen this list.
        </p>
      </div>
    );
  }

  return (
    <div className="absolute bottom-full left-0 right-0 max-h-96 overflow-y-auto border-t border-ivory-300 bg-white shadow-elevated">
      <div className="flex items-center justify-between border-b border-ivory-200 px-4 py-2.5">
        <div>
          <p className="font-body text-sm font-medium text-ink-700">Layout review</p>
          <p className="font-body text-xs text-ink-400">Tap one to see it on the canvas</p>
        </div>
        <button onClick={onClose} className="font-body text-sm text-ink-400 hover:text-ink-600">
          Close
        </button>
      </div>

      {visibleCritical.length > 0 && (
        <div>
          <p className="border-b border-ivory-100 bg-danger/5 px-4 py-1.5 font-body text-xs font-medium uppercase tracking-wide text-danger">
            Needs attention ({visibleCritical.length})
          </p>
          <ul>
            {visibleCritical.map((issue) => (
              <IssueRow key={issue.id} issue={issue} tone="critical" onHighlight={handleHighlight} onDismiss={handleDismiss} />
            ))}
          </ul>
        </div>
      )}

      {visibleWarning.length > 0 && (
        <div>
          <p className="border-b border-ivory-100 bg-warning/5 px-4 py-1.5 font-body text-xs font-medium uppercase tracking-wide text-warning">
            Worth a look ({visibleWarning.length})
          </p>
          <ul>
            {visibleWarning.map((issue) => (
              <IssueRow key={issue.id} issue={issue} tone="warning" onHighlight={handleHighlight} onDismiss={handleDismiss} />
            ))}
          </ul>
        </div>
      )}

      {visibleInfo.length > 0 && (
        <div>
          <button
            onClick={() => setInfoExpanded((e) => !e)}
            className="flex w-full items-center justify-between border-b border-ivory-100 bg-ivory-50 px-4 py-1.5 font-body text-xs font-medium uppercase tracking-wide text-ink-400 hover:bg-ivory-100"
          >
            <span>Minor recommendations ({visibleInfo.length})</span>
            <span aria-hidden>{infoExpanded ? '\u25B4' : '\u25BE'}</span>
          </button>
          {infoExpanded && (
            <ul>
              {visibleInfo.map((issue) => (
                <IssueRow key={issue.id} issue={issue} tone="info" onHighlight={handleHighlight} onDismiss={handleDismiss} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function IssueRow({
  issue,
  tone,
  onHighlight,
  onDismiss,
}: {
  key?: React.Key;
  issue: ValidationIssue;
  tone: 'critical' | 'warning' | 'info';
  onHighlight: (issue: ValidationIssue) => void;
  onDismiss: (id: string) => void;
}) {
  const iconColor = tone === 'critical' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-ink-400';
  const icon = tone === 'critical' ? '\u26A0' : tone === 'warning' ? '\u26A0' : '\u2139';

  return (
    <li className="flex items-start justify-between gap-3 border-b border-ivory-100 px-4 py-3 hover:bg-ivory-50">
      <button
        onClick={() => onHighlight(issue)}
        className="flex flex-1 items-start gap-2 text-left font-body text-sm text-ink-600 hover:text-evergreen-700"
        title="Show this on the canvas"
      >
        <span aria-hidden className={`mt-0.5 ${iconColor}`}>
          {icon}
        </span>
        <span>{issue.message}</span>
      </button>
      <button
        onClick={() => onDismiss(issue.id)}
        title={"Hide this for now \u2014 it won't change your layout, and it will come back if you reopen this list later"}
        className="shrink-0 font-body text-xs text-ink-400 hover:text-ink-600"
      >
        Hide for now
      </button>
    </li>
  );
}
